export const API_FAVORITES_BY_USER = 'API_FAVORITES_BY_USER';
export const API_FAVORITES_BY_TYPE = 'API_FAVORITES_BY_TYPE';
export const API_FAVORITES_UPSERT = 'API_FAVORITES_UPSERT';
export const API_FAVORITES_DELETE = 'API_FAVORITES_DELETE';

export const favoritesByUser = (clientId, userId) => ({
  type: API_FAVORITES_BY_USER,
  id: `${clientId}-${userId}`,
  request: {
    url: '/Favorites',
    method: 'GET',
    params: {
      opts: {
        where: {
          client_id: clientId,
          user_id: userId,
        },
        order: [
          ['object_id', 'ASC'],
        ],
      },
    },
  },
});

/**
 * Transform a DynamicTable order array into properly-qualified SQL column references
 * for sorting through the provider JOIN. Sequelize generates table aliases like
 * `provider`, `provider->location`, `provider->location->organization`, etc.
 * checkLiteralsInOrder will then wrap these as Sequelize.literal() so MySQL can
 * resolve them against the JOIN aliases.
 *
 * Fields that belong to the Favorites table itself are left unchanged.
 */
const FAVORITES_OWN_FIELDS = new Set([
  'id', 'client_id', 'user_id', 'object_type', 'object_id', 'created_at', 'updated_at',
]);

function transformProviderOrder(order) {
  if (!order || order.length === 0) return order;
  return order.map((entry) => {
    const direction = entry[entry.length - 1];
    if (entry.length === 2) {
      const field = entry[0];
      if (!FAVORITES_OWN_FIELDS.has(field)) {
        // Provider-level field — qualify with the `provider` JOIN alias
        return [`\`provider\`.\`${field}\``, direction];
      }
      return entry;
    } else if (entry.length > 2) {
      // Nested association, e.g. ['location', 'city', 'ASC']
      // Build the Sequelize JOIN alias: provider->location, provider->taxonomy, etc.
      const pathParts = entry.slice(0, -1); // strip direction
      const field = pathParts[pathParts.length - 1];
      const associationPath = pathParts.slice(0, -1); // path segments before the field
      const alias = ['provider', ...associationPath].join('->');
      return [`\`${alias}\`.\`${field}\``, direction];
    }
    return entry;
  });
}

export const favoritesByType = (clientId, userId, objectType, options = {}) => {

  const {
    where: typeWhere,
    include: typeInclude,
    attributes: typeAttributes,
    order: rawOrder,
    ...remainingOptions
  } = options;

  // For Providers, rewrite the order so each field is qualified with the correct
  // JOIN alias — prevents 500s from ambiguous or unresolvable column references.
  if (rawOrder) {
    remainingOptions.order = objectType === 'Providers'
      ? transformProviderOrder(rawOrder)
      : rawOrder;
  }
  const include = [
    {
      association: objectType.toLowerCase().substring(0, objectType.length - 1),
      where: typeWhere,
      required: true, 
      include: typeInclude,
      attributes: typeAttributes,
    },
  ];

  if (objectType === 'Providers') {
    // Extract any where clauses from typeInclude so filters pass through to nested associations
    const locationInc = typeInclude && typeInclude.find((inc) => inc.association === 'location');
    const locationWhere = locationInc && locationInc.where;
    const orgInc = locationInc && locationInc.include &&
      locationInc.include.find((inc) => inc.association === 'organization');
    const orgWhere = orgInc && orgInc.where;

    include[0].include = [
      {
        association: 'location',
        attributes: ['city', 'state'],
        where: locationWhere || undefined,
        include: [
          {
            attributes: ['id', 'name'],
            association: 'organization',
            where: orgWhere || undefined,
          },
        ],
      },
      {
        association: 'taxonomy',
        attributes: ['id', 'code', 'specialization', 'classification','doctivity_name'],
      }
    ];
  } else if (objectType === 'Contacts' ) {
      include[0].include = [
        {
          association: 'owner',
          attributes: ['id', 'first_name', 'last_name'],
        },
        
        {
          association: 'location',
          attributes: ['city', 'state'],
          include: [
            {
              attributes: ['id', 'name'],
              association: 'organization',
            },
          ],
          ...typeInclude,
        },
      ];
  } else if (objectType === 'Organizations' ) {
      include[0].include = [
        {
          association: 'primary_location',
          attributes: ['city', 'state'],
          ...typeInclude,
        },
      ];
  }



  return {
    type: API_FAVORITES_BY_TYPE,
    id: `${clientId}-${userId}-${objectType}`,
    request: {
      url: '/Favorites',
      method: 'GET',
      params: {
        opts: {
          where: {
            client_id: clientId,
            user_id: userId,
            object_type: objectType,
          },
          include,
          order: [
            ['id', 'DESC'],
          ],
          limit: 25,
          offset: 0,
          ...remainingOptions,
        },
      },
    },
  };
};

export const upsertFavorite = (clientId, userId, objectType, objectId) => {
  return {
    type: API_FAVORITES_UPSERT,
    id: 0,
    objectType, // used to update lists in reducer
    request: {
      url: '/Favorites',
      method: 'PUT',
      data: {
        client_id: clientId,
        user_id: userId,
        object_type: objectType,
        object_id: objectId,
      },
    },
  };
};

export const deleteFavorite = (id, objectType) => ({
  type: API_FAVORITES_DELETE,
  id,
  objectType, // used to update lists in reducer
  request: {
    url: '/Favorites',
    method: 'DELETE',
    data: [id],
  },
});
